import React, { useState, useEffect, useCallback } from 'react';
import { Fade, TableRow, TableCell, alpha, Typography } from '@mui/material';
import { ShoppingCart as Icon } from '@mui/icons-material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2, fmtDate } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, PurchaseRow } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { fmtBD } from '../../components/common/bdNumber';
 
const ACCENT = '#2196F3';
 
const PurchaseInfoReport: React.FC = () => {
  const [rows, setRows]     = useState<PurchaseRow[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts]     = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({
    date_from: '', date_to: '', product_id: '', category_id: '',
  });
 
  useEffect(() => {
    tradesService.listProducts({ is_active: true }).then(r => setProducts(r.results.map(p => ({ value: p.product_id, label: p.product_name })))).catch(() => {});
    tradesService.listCategories({ is_active: true }).then(r => setCategories(r.results.map(c => ({ value: c.category_id, label: c.category_name })))).catch(() => {});
  }, []);
 
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await reportService.getPurchaseReport(p);
      setRows(res.results.map((r, i) => ({ ...r, sl: i + 1,_qty: qtyLitre(r.quantity_pcs, r.total_litre), }))); 
      setTotals((res.totals || {}) as any);
    } catch {} finally { setLoading(false); }
  }, [filters]);
 
  useEffect(() => { fetchData(); }, [fetchData]);
 
  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return reportService.downloadPurchase(fmt, p);
  };
 
  const fmtLitre = (v: number | string | null | undefined): string => {
    const n = Number(v ?? 0);
    return Number.isInteger(n) ? fmtBD(n, 0) : fmtBD(n, 1);
  };
  const qtyLitre = (pcs: any, litre: any): string =>
    `${fmt2(pcs ?? 0)} Pcs / ${fmtLitre(litre)} L`;

  const qtyLitreSum = (pcs: any, litre: any): string =>
    `${fmt2(pcs ?? 0)} Pcs \n${fmtLitre(litre)} L`;

  const columns: ReportColumn[] = [
    { key: 'sl', label: '#', align: 'center' as const, width: 40 },
    { key: 'purchase_date',       label: 'Date',         format: v => fmtDate(v), noWrap: true },
    { key: 'product_name',        label: 'Product Name',      width: 220 },
    { key: 'category_name',       label: 'Category',     format: v => v || '—' },
    { key: '_qty', label: 'Quantity', align: 'right', format: v => <Typography fontWeight={700} color="#2196F3" fontSize="0.8rem">{v}</Typography> },
    //{ key: 'quantity_pcs',        label: 'Quantity (Pcs)',    align: 'right', format: v => <Typography fontWeight={700} color="#2196F3" fontSize="0.8rem">{v}</Typography> },
    { key: 'purchase_rate',       label: 'Purchase Rate (Tk.)',     align: 'right', format: v => fmt2(v) },
    { key: 'total_purchase_price',label: 'Total (Tk.)',    align: 'right', format: v => <strong>{fmt2(v)}</strong> },
    { key: 'remarks',             label: 'Remarks',      format: v => v || '—' },
    { key: 'created_by',          label: 'Created By',   format: v => <Typography variant="caption" color="text.secondary">{v}</Typography> },
  ];
 
  const summaryCards: SummaryCard[] = [
    { label: 'Records',          value: rows.length,                  color: ACCENT },
    { label: 'Total Quantity', value: qtyLitreSum(totals.total_qty, totals.total_litre), color: '#4CAF50' },
    { label: 'Total Amount (Tk.)', value: fmt2(totals.total_amount ?? 0), color: '#FF9800' },
  ];
 
  const totalsRow = rows.length > 0 && !loading ? (
    <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
      <TableCell colSpan={4} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>TOTALS</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#2196F3' }}>{qtyLitre(totals.total_qty, totals.total_litre)}</TableCell>
      <TableCell />
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{fmt2(totals.total_amount ?? 0)}</TableCell>
      <TableCell colSpan={2} />
    </TableRow>
  ) : undefined;
 
  return (
    <Fade in timeout={400}><div>
      <ReportLayout title="Purchase Information Report" subtitle="Purchase records with date and product filters"
        accentColor={ACCENT} columns={columns} rows={rows} loading={loading}
        tableMinWidth={1500}
        summaryCards={summaryCards} onRefresh={fetchData} onDownload={handleDownload}
        totalsRow={totalsRow} emptyIcon={<Icon sx={{ fontSize: 40 }} />}
        filters={
          <ReportFilterBar accentColor={ACCENT} values={filters}
            onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
            onClear={() => setFilters({ date_from: '', date_to: '', product_id: '', category_id: '' })}
            fields={[
              { type: 'date',   key: 'date_from',   label: 'From' },
              { type: 'date',   key: 'date_to',     label: 'To' },
              { type: 'select', key: 'product_id',  label: 'Product',  options: products,   minWidth: 220 },
              { type: 'select', key: 'category_id', label: 'Category', options: categories, minWidth: 180 },
            ]}
          />
        }
      />
    </div></Fade>
  );
};
export default PurchaseInfoReport ;